import React, { useEffect } from 'react';
import ModalContent from './Components/Modal/ModalContent.tsx';
import ModalBackdrop from './Components/Modal/ModalBackdrop.tsx';
import PortalContainer from './Components/FitAssistantButton/PortalContainer.tsx';
import ProductSliderPortal from './Components/ProductSliderPortal/ProductSliderPortal.tsx';
import RooftopTentPortalContainer from './Components/RoofTopTentButton/PortalContainer.tsx';
import ErrorComponent from './Components/Error/Error.tsx';
import { useAppDispatch, useAppSelector } from './Store/store.ts';
import {
  CurrentStepContentSelector,
  HasErrorSelector,
  InitialDataSelector,
  ShowFitAssistantSelector,
  ShowMoreInfoSelector,
} from './Selectors/FitAssistantSelectors';
import { loadOptionsFromCookie } from './Thunks/LoadOptionsFromCookie.ts';
import Footer from './Components/Footer/Footer.tsx';
import MoreInformation from './Components/MoreInformation/MoreInformation.tsx';
import FitAssistantVariation from './Enums/FitAssistantVariation.ts';
import Awnings from './Variants/Awnings.tsx';
import CarRoofRacks from './Variants/CarRoofRacks.tsx';
import CarSeats from './Variants/CarSeats.tsx';
import DogCrates from './Variants/DogCrates.tsx';
import RoofPlatforms from './Variants/RoofPlatforms.tsx';
import RooftopTents from './Variants/RooftopTents.tsx';
import TruckBedBikeRacks from './Variants/TruckBedBikeRacks.tsx';
import TruckRackSystem from './Variants/TruckRackSystems.tsx';
import TrunkAndSpareTireBikeRacks from './Variants/TrunkAndSpareTireBikeRacks.tsx';
import VanRackSystems from './Variants/VanRackSystems.tsx';
import { getSearchParametersFromQuery, hasOpenFitAssistantQueryParameter } from './Util/QueryFromParameters.ts';
import { setShowFitAssistant } from './Slices/FitAssistantSlice.ts';
import { getWindowData } from '../FitAssistantShared/GetWindowData.js';
import { CookieHelper } from './Helpers/CookieHelper.ts';
import { InstructionTextHelper } from './Helpers/InstructionTextHelper.ts';
import { IsProductPage } from '../FitAssistantShared/IsProductPage.js';
import { loadOptionsFromSolutionId } from './Thunks/LoadOptionsFromSolutionId.ts';
import { Constants } from './Constants.ts';

const FitAssistant = () => {
  const dispatch = useAppDispatch();
  const hasError = useAppSelector(HasErrorSelector);
  const showFitAssistant = useAppSelector(ShowFitAssistantSelector);
  const showMoreInfo = useAppSelector(ShowMoreInfoSelector);
  const currentStepContent = useAppSelector(CurrentStepContentSelector);
  const initialData = useAppSelector(InitialDataSelector);

  const html: HTMLElement = document.querySelector('html')!;
  const body: HTMLElement = document.querySelector('body')!;

  const getVariationComponent = () => {
    switch (initialData.FitAssistantVariation) {
      case FitAssistantVariation.Awnings:
        return <Awnings />;
      case FitAssistantVariation.CarRoofRacks:
        return <CarRoofRacks />;
      case FitAssistantVariation.CarSeats:
        return <CarSeats />;
      case FitAssistantVariation.DogCrates:
        return <DogCrates />;
      case FitAssistantVariation.RoofPlatforms:
        return <RoofPlatforms />;
      case FitAssistantVariation.RooftopTents:
        return <RooftopTents />;
      case FitAssistantVariation.TruckBedBikeRacks:
        return <TruckBedBikeRacks />;
      case FitAssistantVariation.TruckRackSystem:
        return <TruckRackSystem />;
      case FitAssistantVariation.TruckAndSpareTireBikeRacks:
        return <TrunkAndSpareTireBikeRacks />;
      case FitAssistantVariation.VanRackSystems:
        return <VanRackSystems />;
      default:
        return <></>;
    }
  };

  const ensureLoad = async () => {
    const hasSolutionIdQueryParam = getSearchParametersFromQuery(Constants.QueryParameters.SOLUTION_ID);

    if (!hasSolutionIdQueryParam) {
      await dispatch(loadOptionsFromCookie());
    } else {
      await dispatch(loadOptionsFromSolutionId());
    }
  };

  useEffect(() => {
    ensureLoad();
    let cookie = CookieHelper.GetCookies()?.cookie;
    if (hasOpenFitAssistantQueryParameter() && (getWindowData().SitecoreSiteName !== 'Thule' || !cookie)) {
      dispatch(setShowFitAssistant(true));
    }

    if (IsProductPage && InstructionTextHelper.HasInstructionText(initialData.FitAssistantVariation)) {
      InstructionTextHelper.ShowInstructionText();
    }
  }, []);

  useEffect(() => {
    if (showFitAssistant) {
      html.classList.add('overflow-hidden');
      body.classList.add('overflow-hidden');
    } else {
      html.classList.remove('overflow-hidden');
      body.classList.remove('overflow-hidden');
    }
  }, [showFitAssistant]);

  return (
    <>
      {initialData.FitAssistantVariation === FitAssistantVariation.RooftopTents && IsProductPage ? (
        <RooftopTentPortalContainer />
      ) : (
        <PortalContainer />
      )}
      <ProductSliderPortal />
      <ModalContent>
        {showFitAssistant && getVariationComponent()}
        {showMoreInfo && <MoreInformation />}
        {hasError && <ErrorComponent />}
        {!hasError && !showMoreInfo && currentStepContent?.MoreInfoContent && <Footer />}
      </ModalContent>
      <ModalBackdrop />
    </>
  );
};

export default FitAssistant;
